import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { FamilyDocument } from './useOptimizedDocumentsWrapper';

interface CategoryCount {
  category: string;
  count: number;
}

interface DocumentsPageData {
  documents: FamilyDocument[];
  categories: CategoryCount[];
}

/**
 * Hook optimisé qui charge toutes les données de la page documents en une seule requête RPC
 */
export function useDocumentsPageData(familyId?: string) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['documents-page-data', familyId],
    queryFn: async (): Promise<DocumentsPageData> => {
      if (!familyId) {
        return { documents: [], categories: [] };
      }

      const { data, error } = await supabase.rpc('get_documents_data', {
        p_family_id: familyId
      });

      if (error) {
        console.error('Error fetching documents data:', error);
        throw error;
      }

      const result = (data as unknown) as DocumentsPageData;

      // Pré-remplir le cache individuel
      if (result.documents) {
        queryClient.setQueryData(['optimized-documents', familyId], result.documents);
      }

      return result;
    },
    enabled: !!familyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}
